




import React, { useState, useEffect } from "react";
import "./GalleryCards.css";
import { aboutUsService } from "../../../../Service/aboutUsService";
import { useCompressedUpload } from '../../../../utils/Compression/useCompressedUpload';
import { formatBytes, getFileInfo, monitorReceivedImage } from '../../../../utils/Compression/mediaCompression';

const Gallery = () => {
    const [mediaItems, setMediaItems] = useState([]);
    const [aboutUsId, setAboutUsId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingMedia, setEditingMedia] = useState(null);
    const [editFile, setEditFile] = useState(null);

    // Compression states
    const [originalFileInfo, setOriginalFileInfo] = useState(null);
    const [compressedFileInfo, setCompressedFileInfo] = useState(null);
    const [isCompressing, setIsCompressing] = useState(false);

    // Quality comparison state
    const [showQualityCompare, setShowQualityCompare] = useState(false);
    const [comparisonImage, setComparisonImage] = useState(null);

    // Helper function to get full image URL
    const getFullImageUrl = (imagePath) => {
        if (!imagePath) return "";
        if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
            return imagePath;
        }
        if (imagePath.startsWith("food/")) {
            return `https://backend.maamaas.com/${imagePath}`;
        }
        return `https://backend.maamaas.com/food/${imagePath}`;
    };

    // Fetch gallery data with monitoring
    const fetchGallery = async () => {
        try {
            setLoading(true);

            const response = await aboutUsService.getAboutUs();
            const data = response.data || response;


            if (data && data.aboutUsId) {
                setAboutUsId(data.aboutUsId);
                const images = data.images || [];

                // Process image URLs
                const processedImages = images.map(img => ({
                    id: img.id,
                    mediaUrl: img.mediaUrl, // Already full S3 URL
                    mediaFile: img.mediaFile
                }));


                // Monitor each image's size and quality
                for (const img of processedImages) {
                    await monitorReceivedImage(img.mediaUrl, `Gallery_Image_${img.id}`);
                }

                setMediaItems(processedImages);
            } else {
                setMediaItems([]);
            }
        } catch (error) {
            console.error("Fetch gallery error:", error);
            setMediaItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGallery();
    }, []);

    // Handle file selection with compression
    const handleFileSelect = async (e) => {
        let file = e.target.files[0];
        if (!file) return;

     
        if (!file.type.startsWith('image/')) {
            alert("Please select an image file (JPG, PNG, GIF, etc.)");
            return;
        }

        // Store original file info
        const originalInfo = {
            name: file.name,
            type: file.type,
            size: file.size,
            sizeFormatted: formatBytes(file.size),
            lastModified: new Date(file.lastModified).toLocaleString()
        };
        setOriginalFileInfo(originalInfo);


        setIsCompressing(true);

        try {
            // Simulate progress
            const interval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 100);

            // Compress the image
            const compressedFile = await compressImage(file);

            clearInterval(interval);
            setUploadProgress(100);

            // Store compressed file info
            const compressedInfo = {
                name: compressedFile.name,
                type: compressedFile.type,
                size: compressedFile.size,
                sizeFormatted: formatBytes(compressedFile.size)
            };
            setCompressedFileInfo(compressedInfo);


            // Calculate savings
            const savedBytes = file.size - compressedFile.size;
            const savedPercent = ((savedBytes / file.size) * 100).toFixed(1);

   
            setSelectedFile(compressedFile);

            // Show quality comparison option for large files
        

        } catch (error) {
            console.error('%c❌ Compression failed, using original file:', 'color: #f44336; font-weight: bold', error);
            setSelectedFile(file);
        } finally {
            setIsCompressing(false);
            setTimeout(() => setUploadProgress(0), 500);
        }
    };

    // Compression function
    const compressImage = async (file) => {
        const fileSizeMB = file.size / (1024 * 1024);

        // Determine compression strategy
        let options;
        let strategy = '';

        if (fileSizeMB <= 1) {
            return file;
        }
        else if (fileSizeMB <= 3) {
            strategy = 'LIGHT';
            options = {
                maxSizeMB: 0.8,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.85
            };
        }
        else if (fileSizeMB <= 5) {
            strategy = 'MEDIUM';
            options = {
                maxSizeMB: 1.0,
                maxWidthOrHeight: 1600,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.8
            };
        }
        else {
            strategy = 'AGGRESSIVE';
            options = {
                maxSizeMB: 1.2,
                maxWidthOrHeight: 1280,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.7
            };
        }


        try {
            const imageCompression = (await import('browser-image-compression')).default;
            const compressed = await imageCompression(file, options);
            return compressed;
        } catch (err) {
            console.error('Compression error:', err);
            return file;
        }
    };

    // Add new media with compression
    const handleAddMedia = async () => {
        if (!selectedFile) {
            alert("Please select a file to upload");
            return;
        }

        try {
            setUploading(true);

            const formData = new FormData();
            const aboutUsData = { aboutUsId: aboutUsId };
            formData.append("aboutUsData", JSON.stringify(aboutUsData));
            formData.append("images", selectedFile);

            await aboutUsService.updateGallery(aboutUsId, formData);


            setSelectedFile(null);
            setOriginalFileInfo(null);
            setCompressedFileInfo(null);
            setAddDialogOpen(false);
            await fetchGallery();
            alert("Image added successfully!");
        } catch (error) {
            console.error("Add media error:", error);
            alert(error.message || "Failed to add media");
        } finally {
            setUploading(false);
        }
    };

    // Delete media
    const handleDeleteMedia = async (imageId, imageName) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        try {
            setUploading(true);


            await aboutUsService.deleteImage(imageId);


            await fetchGallery();
            alert("Image deleted successfully!");
        } catch (error) {
            console.error("Delete media error:", error);
            alert(error.message || "Failed to delete media");
        } finally {
            setUploading(false);
        }
    };

    // Open edit dialog
    const handleEditClick = (item, index) => {
        setEditingMedia({ ...item, index });
        setEditFile(null);
        setEditDialogOpen(true);
    };

    // Handle edit file selection with compression
    const handleEditFileSelect = async (e) => {
        let file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert("Please select an image file");
            return;
        }


        try {
            const compressed = await compressImage(file);
            setEditFile(compressed);
        } catch (error) {
            console.error('Compression failed:', error);
            setEditFile(file);
        }
    };

    // Update media
    const handleUpdateMedia = async () => {
        if (!editFile) {
            alert("Please select a new file");
            return;
        }

        try {
            setUploading(true);

            // Add the new compressed image
            const formData = new FormData();
            const aboutUsData = { aboutUsId: aboutUsId };
            formData.append("aboutUsData", JSON.stringify(aboutUsData));
            formData.append("images", editFile);

            await aboutUsService.updateGallery(aboutUsId, formData);

            // Delete the old image
            await aboutUsService.deleteImage(editingMedia.id);


            setEditDialogOpen(false);
            setEditingMedia(null);
            setEditFile(null);
            await fetchGallery();
            alert("Image replaced successfully!");
        } catch (error) {
            console.error("Update media error:", error);
            alert("Failed to update media");
        } finally {
            setUploading(false);
        }
    };

    // Open preview with quality info
    const handlePreview = async (item) => {
        setSelectedMedia(item);
        setPreviewDialogOpen(true);

        // Monitor and log image quality when previewed
        await monitorReceivedImage(item.mediaUrl, `Gallery_Image_${item.id}`);
    };

    if (loading) {
        return (
            <div className="gallery-loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <section className="gallery-section">
            <div className="gallery-container">
                {/* Header */}
                <div className="gallery-header">
                    <h2 className="gallery-title">Gallery</h2>

                    <button
                        className="gallery-add-button"
                        onClick={() => setAddDialogOpen(true)}
                    >
                        <svg className="gallery-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        Add Image
                    </button>
                </div>

              

                {/* Media Grid */}
                {mediaItems.length === 0 ? (
                    <div className="gallery-empty-state">
                        <div className="gallery-empty-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-4-3 3-4-4-5 5" />
                            </svg>
                        </div>
                        <h3 className="gallery-empty-title">No Images Yet</h3>
                        <button className="gallery-add-button gallery-empty-button" onClick={() => setAddDialogOpen(true)}>
                            <svg className="gallery-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add Your First Image
                        </button>
                    </div>
                ) : (
                    <div className="gallery-grid">
                        {mediaItems.map((item, index) => (
                            <div className="gallery-card" key={item.id || index}>
                                <div className="gallery-card-image-wrapper">
                                    <img
                                        src={item.mediaUrl}
                                        alt={`Gallery ${index + 1}`}
                                        className="gallery-card-image"
                                        onClick={() => handlePreview(item)}
                                        style={{ cursor: 'pointer' }}
                                    />

                                    {/* Action Buttons */}
                                    <div className="gallery-card-actions">
                                        <button
                                            className="gallery-action-btn gallery-edit-btn"
                                            onClick={() => handleEditClick(item, index)}
                                            title="Replace Image"
                                        >
                                            <svg className="gallery-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                            </svg>
                                        </button>
                                        <button
                                            className="gallery-action-btn gallery-delete-btn"
                                            onClick={() => handleDeleteMedia(item.id, `Image ${index + 1}`)}
                                            title="Delete"
                                        >
                                            <svg className="gallery-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Media Modal with Compression Info */}
            {addDialogOpen && (
                <div className="gallery-modal-overlay" onClick={() => setAddDialogOpen(false)}>
                    <div className="gallery-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="gallery-modal-header">
                            <h3>Add Image</h3>
                            <button className="gallery-modal-close" onClick={() => setAddDialogOpen(false)}>×</button>
                        </div>

                        <div className="gallery-modal-body">
                            <div className="gallery-modal-field">
                                <label className="gallery-modal-label">
                                    Select Image
                                </label>
                                <div className="gallery-file-upload">
                                    <input
                                        type="file"
                                        id="gallery-file-upload"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="gallery-file-upload" className="gallery-file-upload-label">
                                        <svg className="gallery-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        {selectedFile ? selectedFile.name : 'Choose Image File'}
                                    </label>
                                </div>
                          
                            </div>

                            {uploading && (
                                <div className="gallery-upload-progress">
                                    <div className="gallery-progress-bar">
                                        <div className="gallery-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                                    </div>
                                    <div className="gallery-progress-text">Uploading... {uploadProgress}%</div>
                                </div>
                            )}
                        </div>

                        <div className="gallery-modal-footer">
                            <button className="gallery-cancel-btn" onClick={() => {
                                setAddDialogOpen(false);
                                setSelectedFile(null);
                                setOriginalFileInfo(null);
                                setCompressedFileInfo(null);
                            }}>
                                Cancel
                            </button>
                            <button
                                className="gallery-save-btn"
                                onClick={handleAddMedia}
                                disabled={uploading || !selectedFile || isCompressing}
                            >
                                {isCompressing ? "Compressing..." : uploading ? "Uploading..." : "Add Image"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Media Modal */}
            {editDialogOpen && (
                <div className="gallery-modal-overlay" onClick={() => setEditDialogOpen(false)}>
                    <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gallery-modal-header">
                            <h3>Replace Image</h3>
                            <button className="gallery-modal-close" onClick={() => setEditDialogOpen(false)}>×</button>
                        </div>

                        <div className="gallery-modal-body">
                            <div className="gallery-modal-field">
                                <label className="gallery-modal-label">
                                    Select New Image
                                </label>
                                <div className="gallery-file-upload">
                                    <input
                                        type="file"
                                        id="gallery-edit-file-upload"
                                        accept="image/*"
                                        onChange={handleEditFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="gallery-edit-file-upload" className="gallery-file-upload-label">
                                        <svg className="gallery-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        {editFile ? editFile.name : 'Choose New Image'}
                                    </label>
                                </div>
                                {editFile && (
                                    <div className="gallery-file-info">
                                        Size: {(editFile.size / (1024 * 1024)).toFixed(2)} MB
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="gallery-modal-footer">
                            <button className="gallery-cancel-btn" onClick={() => setEditDialogOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="gallery-save-btn"
                                onClick={handleUpdateMedia}
                                disabled={uploading || !editFile}
                            >
                                {uploading ? "Updating..." : "Replace Image"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal with Quality Info */}
            {previewDialogOpen && selectedMedia && (
                <div className="gallery-modal-overlay" onClick={() => setPreviewDialogOpen(false)}>
                    <div className="gallery-preview-modal" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                        <button className="gallery-preview-close" onClick={() => setPreviewDialogOpen(false)}>×</button>


                        <img
                            src={selectedMedia?.mediaUrl}
                            alt="Preview"
                            className="gallery-preview-media"
                            style={{ cursor: 'zoom-in', maxWidth: '90vw', maxHeight: '90vh' }}
                            onClick={(e) => {
                                // Open in new tab for full inspection
                                window.open(selectedMedia?.mediaUrl, '_blank');
                            }}
                            onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                            }}
                        />


                    </div>
                </div>
            )}
        </section>
    );
};

export default Gallery;